import { sharedSchema } from "@shared-database/pgSchema";
import { leoID, timestampColumns } from "@shared-database/utils";
import type { InferInsertModel, InferSelectModel } from "drizzle-orm";
import {
  index,
  integer,
  jsonb,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";

/**
 * Content-addressed cache for raw graph extraction results.
 *
 * The cache is deliberately not profile-scoped: identical profile-ingested
 * extracted text can be reused across direct profile uploads and profile
 * SharePoint syncs. Profile ownership is established later when the cached
 * entities/relations are materialized into `KnowledgeEntity`,
 * `KnowledgeRelation`, sources, and communities for a specific AI profile.
 *
 * The unique key includes the content hash, extraction schema/prompt version,
 * and graph model so prompt/model changes cannot accidentally replay stale
 * extraction semantics. `contentHash` is not a checksum of the raw uploaded
 * PDF/Office bytes; it is derived from the canonical ordered `DocumentChunk`
 * text that graph extraction actually sees.
 *
 * Important invariant: a row in this table means "the entire file-level raw
 * extraction is complete and safe to materialize." Callers can hydrate it and
 * proceed directly to profile-owned graph rows. Do not store partial batch
 * progress here; doing so would overload the meaning of a cache hit and risks
 * materializing an incomplete graph as if it were complete.
 */
export const GraphExtractionCache = sharedSchema.table(
  "GraphExtractionCache",
  {
    // Mixins
    ...timestampColumns,

    // Properties
    id: leoID("id"),

    /**
     * Stable hash of the canonical ordered `DocumentChunk.content` text used
     * for graph extraction. This is the reuse boundary; two files with the
     * same processed text and chunk order can share the expensive raw
     * extraction result even when their raw file bytes or file ids differ.
     *
     * This is intentionally separate from file checksums: graph extraction is
     * a function of extracted/chunked text, not the original PDF/Office binary.
     */
    contentHash: text("contentHash").notNull(),

    /**
     * Version of the graph extraction schema/prompt/post-processing contract.
     * Bump this whenever the cached payload shape or extraction semantics
     * change enough that prior rows should not be reused.
     */
    extractionVersion: text("extractionVersion").notNull(),

    /**
     * Deployment/model used for raw extraction. Kept in the unique key because
     * different graph models can produce meaningfully different entity and
     * relation sets from the same content.
     */
    graphModel: text("graphModel").notNull(),

    /**
     * Serialized raw extraction payload. It stores chunk indexes rather than
     * `DocumentChunk.id` values so the same extraction can be hydrated against
     * a different profile's chunk rows before profile-owned graph rows are
     * materialized.
     */
    extraction: jsonb("extraction").$type<unknown>().notNull(),

    /** Number of chunks covered by this extraction payload. */
    chunksProcessed: integer("chunksProcessed").notNull(),

    /**
     * Token accounting for raw graph extraction. These values belong to the
     * reusable cache entry; profile-specific graph file state records the
     * materialization/job counters shown in profile progress UI.
     */
    graphInputTokens: integer("graphInputTokens").notNull().default(0),
    graphOutputTokens: integer("graphOutputTokens").notNull().default(0),

    /** Refreshed when a cache row is inserted or replaced. */
    updatedAt: timestamp("updatedAt", { mode: "date" }).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("GraphExtractionCache_uniqueIdx").on(
      table.contentHash,
      table.extractionVersion,
      table.graphModel,
    ),
    index("GraphExtractionCache_contentHashIdx").on(table.contentHash),
  ],
);

/**
 * Content-addressed checkpoint cache for individual raw extraction batches.
 *
 * This table intentionally has a narrower contract than
 * `GraphExtractionCache`: a row here means only "this one LLM batch succeeded
 * for this content hash/version/model/batch index." It is resumability state,
 * not a materializable graph. Full-file graph extraction still materializes
 * only after every expected batch has either been loaded from this table or
 * completed during the current attempt, followed by aggregation, filtering, and
 * final full-file cache write.
 *
 * The separation matters for large files. A 6-hour extraction may complete
 * hundreds of batches before one provider call fails, the app restarts, or a
 * queue attempt is abandoned. Without batch checkpoints, the next retry would
 * discard all successful LLM work and start from zero. With this table, retries
 * process only missing batches while the existing `GraphExtractionCache`
 * invariant stays simple and safe: whole-file rows are complete rows.
 *
 * The cache is not profile-scoped for the same reason as the full-file cache:
 * batch output depends on canonical ordered chunk text, extraction version,
 * model, and batch boundaries, not on raw file bytes or on the AI profile that
 * requested materialization.
 */
export const GraphExtractionBatchCache = sharedSchema.table(
  "GraphExtractionBatchCache",
  {
    // Mixins
    ...timestampColumns,

    // Properties
    id: leoID("id"),
    /**
     * Same extracted-text hash used by `GraphExtractionCache.contentHash`.
     * It identifies the whole ordered chunk text for the file; `batchIndex`
     * then selects which chunk batch this checkpoint belongs to.
     */
    contentHash: text("contentHash").notNull(),
    extractionVersion: text("extractionVersion").notNull(),
    graphModel: text("graphModel").notNull(),
    batchIndex: integer("batchIndex").notNull(),

    /**
     * Serialized extraction for this single chunk batch. The payload includes
     * the batch chunk indexes so callers can reject stale checkpoints if batch
     * sizing changes under the same extraction version.
     */
    extraction: jsonb("extraction").$type<unknown>().notNull(),

    /** Number of chunks covered by this batch payload. */
    chunksProcessed: integer("chunksProcessed").notNull(),
    graphInputTokens: integer("graphInputTokens").notNull().default(0),
    graphOutputTokens: integer("graphOutputTokens").notNull().default(0),
    updatedAt: timestamp("updatedAt", { mode: "date" }).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("GraphExtractionBatchCache_uniqueIdx").on(
      table.contentHash,
      table.extractionVersion,
      table.graphModel,
      table.batchIndex,
    ),
    index("GraphExtractionBatchCache_contentHashIdx").on(table.contentHash),
  ],
);

// Types
export type GraphExtractionCacheType = InferSelectModel<
  typeof GraphExtractionCache
>;
export type GraphExtractionCacheInsertType = InferInsertModel<
  typeof GraphExtractionCache
>;
export type GraphExtractionBatchCacheType = InferSelectModel<
  typeof GraphExtractionBatchCache
>;
export type GraphExtractionBatchCacheInsertType = InferInsertModel<
  typeof GraphExtractionBatchCache
>;
